import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { can } from "../../../utils/can";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CMSFaqManager from "../../../components/common/CMSFaqManager";

const PAGE_TYPE = "state_home_page";
const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function CMSStateHomePage() {
  const API_URL = (process.env.REACT_APP_BASE_URL_API || "").replace(/\/$/, "");
  const token = localStorage.getItem("adminToken");
  const PAGE_SIZE = 10;

  const [states, setStates] = useState([]);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const emptyForm = {
    id: null, state_id: "", slug: "", aboutTitle1: "", aboutTitle2: "",
    content: "", whyChooseTitle: "", whyChooseUs: "",
    sectionTitle: "", sectionContent: "",
    faqTitle: "", faqs: [],
    metaTitle: "", metaKeywords: "",
    customScript: "", metaDescription: "", status: "Active",
  };
  const [form, setForm] = useState(emptyForm);

  const fetchStates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms-page/home-states`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) setStates(result.data || []);
    } catch (err) { console.error(err); }
  }, [API_URL, token]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/cms-page-list?pageType=${PAGE_TYPE}&page=${page}&pageSize=${pageSize}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      if (result.success) {
        setList(result.data || []);
        setTotalPages(result.totalPages || 1);
      }
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, [API_URL, token, page, pageSize]);

  useEffect(() => { fetchStates(); }, [fetchStates]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (can("cms_state_home_page", form.id ? "edit" : "create") === false) {
      alert("You don't have permission."); return;
    }
    if (!form.state_id) { alert("Please select a state."); return; }
    setLoading(true); setMessage("");

    const url = form.id
      ? `${API_URL}/api/admin/cms-page-list/update/${form.id}`
      : `${API_URL}/api/admin/cms-page-create`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pageType: PAGE_TYPE, ...form, id: undefined }),
      });
      const result = await res.json();
      if (result.success) {
        setMessage("Saved!"); setForm(emptyForm); fetchList();
      } else { alert(result.message || "Error"); }
    } catch (err) { alert("Server error."); }
    finally { setLoading(false); }
  };

  const handleEdit = (row) => {
    if (can("cms_state_home_page", "edit") === false) { alert("No permission."); return; }
    setForm({
      id: row._id, state_id: row.state_id?._id || "", slug: row.slug || "",
      aboutTitle1: row.aboutTitle1 || "", aboutTitle2: row.aboutTitle2 || "",
      content: row.content || "", whyChooseTitle: row.whyChooseTitle || "", whyChooseUs: row.whyChooseUs || "",
      sectionTitle: row.sectionTitle || "", sectionContent: row.sectionContent || "",
      faqTitle: row.faqTitle || "", faqs: Array.isArray(row.faqs) ? row.faqs : [],
      metaTitle: row.metaTitle || "", metaKeywords: row.metaKeywords || "",
      customScript: row.customScript || "",
      metaDescription: row.metaDescription || "", status: row.status || "Active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (can("cms_state_home_page", "delete") === false) { alert("No permission."); return; }
    if (!window.confirm("Delete this entry?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/cms-page-list/delete/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) fetchList();
      else alert(result.message);
    } catch (err) { alert("Server error."); }
  };

  const columns = [
    { label: "S.No", width: 50, render: (r, i) => (page - 1) * pageSize + i + 1 },
    { label: "State", render: (r) => r.state_id?.name || "-" },
    { label: "Slug", render: (r) => r.slug || "-" },
    { label: "About Title 1", render: (r) => r.aboutTitle1 || "-" },
    { label: "Status", width: 90, render: (r) => <span className={r.status === "Active" ? "text-success" : "text-danger"}>{r.status}</span> },
    {
      label: "Action", width: 120, render: (r) => (
        <ul className="actionlist p-0">
          <li><a href="#" onClick={(e) => { e.preventDefault(); handleEdit(r); }}><i className="fa fa-edit" /></a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); handleDelete(r._id); }}><i className="fa fa-trash" /></a></li>
        </ul>
      )
    },
  ];

  return (
    <div className="container body"><div className="main_container">
      <Sidebar /><Header pageTitle="CMS - State Home Page" />
      <div className="right_col" role="main">
        <div className="x_panel"><div className="x_title"><h2>{form.id ? "Edit" : "Add"} State Home Page</h2><div className="clearfix" /></div>
          <div className="x_content"><form onSubmit={handleSubmit}><div className="row">
            <div className="col-12 col-sm-4 mb-3">
              <label>Select Home Page State</label>
              <select
                className="form-select"
                value={form.state_id}
                onChange={(e) => {
                  const selected = states.find((s) => s._id === e.target.value);
                  const rawSlug = selected ? slugify(selected.name) : "";
                  const slug = rawSlug ? (rawSlug.endsWith("-matrimony") ? rawSlug : `${rawSlug}-matrimony`) : "";
                  setForm({
                    ...form,
                    state_id: e.target.value,
                    slug,
                  });
                }}
              >
                <option value="">Select</option>
                {states.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-12 col-sm-4 mb-3"><label>Slug</label><input type="text" className="form-control" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} /></div>
            <div className="col-12 col-sm-4 mb-3"><label>About Title 1</label><input type="text" className="form-control" value={form.aboutTitle1} onChange={(e) => setForm({ ...form, aboutTitle1: e.target.value })} /></div>
            <div className="col-12 col-sm-4 mb-3"><label>About Title 2</label><input type="text" className="form-control" value={form.aboutTitle2} onChange={(e) => setForm({ ...form, aboutTitle2: e.target.value })} /></div>
            <div className="col-12 mb-3"><label>Content</label><CKEditor editor={ClassicEditor} data={form.content} onChange={(_, ed) => setForm((p) => ({ ...p, content: ed.getData() }))} /></div>
            <div className="col-12 col-sm-6 mb-3"><label>Why Choose Title / Heading (Optional)</label><input type="text" className="form-control" value={form.whyChooseTitle} onChange={(e) => setForm({ ...form, whyChooseTitle: e.target.value })} placeholder="e.g. Best Marriage Bureau in Bihar" /></div>
            <div className="col-12 mb-3"><label>Why Choose Us Content</label><CKEditor editor={ClassicEditor} data={form.whyChooseUs} onChange={(_, ed) => setForm((p) => ({ ...p, whyChooseUs: ed.getData() }))} /></div>
            <div className="col-12 col-sm-6 mb-3"><label>Section 3 Title / Bottom Heading (Optional)</label><input type="text" className="form-control" value={form.sectionTitle} onChange={(e) => setForm({ ...form, sectionTitle: e.target.value })} placeholder="e.g. Best Matrimonial Services in Bihar" /></div>
            <div className="col-12 mb-3"><label>Section 3 Content / Bottom Content (Optional)</label><CKEditor editor={ClassicEditor} data={form.sectionContent} onChange={(_, ed) => setForm((p) => ({ ...p, sectionContent: ed.getData() }))} /></div>

            <CMSFaqManager
              faqTitle={form.faqTitle}
              onChangeFaqTitle={(faqTitle) => setForm((p) => ({ ...p, faqTitle }))}
              faqs={form.faqs}
              onChangeFaqs={(faqs) => setForm((p) => ({ ...p, faqs }))}
            />

            <div className="col-md-12 mb-3"><label>Meta Title</label><input type="text" className="form-control" value={form.metaTitle} onChange={(e) => setForm({ ...form, metaTitle: e.target.value })} /></div>
            <div className="col-md-12 mb-3"><label>Meta Keywords</label><input type="text" className="form-control" value={form.metaKeywords} onChange={(e) => setForm({ ...form, metaKeywords: e.target.value })} /></div>
            <div className="col-md-12 mb-3">
              <label>Custom Scripts (GTM, Schema, Analytics)</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="<script type=&quot;application/ld+json&quot;>&#10;...&#10;</script>"
                value={form.customScript}
                onChange={(e) => setForm({ ...form, customScript: e.target.value })}
              />
            </div>
            <div className="col-md-12 mb-3"><label>Meta Description</label><textarea className="form-control" rows={2} value={form.metaDescription} onChange={(e) => setForm({ ...form, metaDescription: e.target.value })} /></div>
            <div className="col-12 col-sm-4 mb-3"><label>Status</label><select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div>
            <div className="col-12 text-right">
              <button type="submit" className="btn sitebtn me-2" disabled={loading}>{loading ? "Saving..." : "Submit"}</button>
              {form.id && <button type="button" className="btn btn-secondary" onClick={() => setForm(emptyForm)}>Cancel</button>}
              {message && <span className="text-success ms-2">{message}</span>}
            </div>
          </div></form></div>
        </div>
        <div className="x_panel"><div className="x_title"><h2>State Home Page List</h2><div className="clearfix" /></div>
          <div className="x_content"><AdminDataTable columns={columns} data={list} page={page} pageSize={pageSize} totalPages={totalPages} loading={listLoading} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} /></div>
        </div>
      </div>
      <Footer />
    </div></div>
  );
}
